import { Component, effect, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VideoBackgroundComponent } from 'src/app/shared/ui-common/video-background/video-background.component';
import { SharedService } from 'src/app/shared/data/shared.service';
import { SurveySliderComponent } from './survey-slider/survey-slider.component';
import {
  BodyPrefill,
  SurveyAnswer,
  SurveyIntervention,
  SurveySession,
  SurveySessionAnswer
} from '../data/survey.model';
import { ButtonComponent } from '../../../shared/ui-common/button/button.component';
import { PillComponent } from 'src/app/shared/ui-common/pill/pill.component';
import { PillProgressBarComponent } from 'src/app/shared/ui-common/pill-progress-bar/pill-progress-bar.component';
import { PillService } from 'src/app/shared/data/pill.service';
import { MenuItem, SurveyType } from 'src/app/shared/data/models/menu-item.model';
import { SurveyBodyComponent } from './survey-body/survey-body.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SurveyService } from '../data/survey.service';
import { InterventionService } from '../data/intervention.service';
import { BodyPart } from 'src/app/shared/data/models/body-parts.model';
import { BodyPartLabel } from 'src/app/shared/data/models/body-parts.model';
import { SurveyFeedbackComponent } from './survey-feedback/survey-feedback.component';
import { FeedbackFormValue, SurveyFeedbackRecipients } from '../data/survey-feedback.model';
import { Account } from 'src/app/shared/data/models/account.model';
import { SurveyCustomComponent } from './survey-custom/survey-custom.component';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';

enum SurveyStep {
  Survey = 1,
  Feedback = 2,
  Done = 3,
  Intervention = 4,
  Error = 5
}

@Component({
  selector: 'app-feature-survey',
  imports: [
    CommonModule,
    VideoBackgroundComponent,
    SurveySliderComponent,
    ButtonComponent,
    PillComponent,
    PillProgressBarComponent,
    SurveyBodyComponent,
    SurveyFeedbackComponent,
    SurveyCustomComponent,
    TranslatePipe
  ],
  templateUrl: './feature-survey.component.html',
  styleUrl: './feature-survey.component.scss',
})
export class FeatureSurveyComponent implements OnInit, OnDestroy {
  pillProgress = signal<number>(10);
  pillExpanded = false;
  answerOptions = [
    {
      text: 'WebApp.v1.Survey.Mind.Answer.Option.AllTheTime',
      value: 5,
    },
    {
      text: 'WebApp.v1.Survey.Mind.Answer.Option.MostOfTheTime',
      value: 4,
    },
    {
      text: 'WebApp.v1.Survey.Mind.Answer.Option.MoreThanHalfOfTheTime',
      value: 3,
    },
    {
      text: 'WebApp.v1.Survey.Mind.Answer.Option.LessThanHalfOfTheTime',
      value: 2,
    },
    {
      text: 'WebApp.v1.Survey.Mind.Answer.Option.SomeOfTheTime',
      value: 1,
    },
    {
      text: 'WebApp.v1.Survey.Mind.Answer.Option.AtNoTime',
      value: 0,
    },
  ];

  survey = {
    id: 1,
    questions: [
      {
        text: 'WebApp.v1.Survey.Mind.Question.Mood',
        description: 'WebApp.v1.Survey.Mind.Question.Mood.Description',
        video: 'mountain',
        icon: 'mood',
        answer: -1,
        timeTaken: 0,
      },
      {
        text: 'WebApp.v1.Survey.Mind.Question.Relaxation',
        description: 'WebApp.v1.Survey.Mind.Question.Relaxation.Description',
        video: 'forest',
        icon: 'relaxation',
        answer: -1,
        timeTaken: 0,
      },
      {
        text: 'WebApp.v1.Survey.Mind.Question.Energy',
        description: 'WebApp.v1.Survey.Mind.Question.Energy.Description',
        video: 'lake',
        icon: 'energy',
        answer: -1,
        timeTaken: 0,
      },
      {
        text: 'WebApp.v1.Survey.Mind.Question.Sleep',
        description: 'WebApp.v1.Survey.Mind.Question.Sleep.Description',
        video: 'nightLake',
        icon: 'sleep',
        answer: -1,
        timeTaken: 0,
      },
      {
        text: 'WebApp.v1.Survey.Mind.Question.Motivation',
        description: 'WebApp.v1.Survey.Mind.Question.Motivation.Description',
        video: 'green_hills',
        icon: 'motivation',
        answer: -1,
        timeTaken: 0,
      }
    ]
  };

  videoSource = 'mountain';
  surveySteps = SurveyStep;
  surveyStepSignal = signal<SurveyStep>(SurveyStep.Survey);
  currentMenuItemSignal = signal<MenuItem | null>(null);
  nextMenuItemSignal = signal<MenuItem | null>(null);
  menuItemType = SurveyType;
  isOnDemand = false;
  onDemandType: 'who5' | 'body' | null = null;
  surveyIntervention: SurveyIntervention | null = null;
  bodyPrefill!: BodyPrefill;
  surveySession!: SurveySession;
  recipients!: SurveyFeedbackRecipients;
  accountData!: Account;
  allVideoSources: string[] = [];
  disabled = false;
  surveyAnswer: SurveyAnswer | null = null;
  answerId = 0;

  private routeSubscription?: Subscription;

  get showLogo(): boolean {
    return window.innerWidth >= 768 && window.innerHeight > 900;
  }

  get showAnonymous(): boolean {
    return this.surveyStepSignal() !== SurveyStep.Feedback && window.innerWidth >= 768;
  }

  get nextTitle(): string {
    return this.isOnDemand ? 'WebApp.v1.Shared.Pill.RequestCall.Title' : this.nextMenuItemSignal()?.title ?? '';
  }

  get nextTitleIcon(): string {
    return this.isOnDemand ? 'fa-light fa-mobile-signal-out' : this.nextMenuItemSignal()?.icon ?? '';
  }

  constructor(
    private sharedService: SharedService,
    private pillService: PillService,
    private route: ActivatedRoute,
    private router: Router,
    private surveyService: SurveyService,
    private interventionService: InterventionService
  ) {
    effect(() => {
      if (this.surveyStepSignal() !== SurveyStep.Survey) {
        return;
      }

      if (this.isOnDemand) {
        const menuItems = this.pillService.getMenuItems();
        const menuItem = menuItems.find(item => item.type === this.onDemandType);

        if (menuItem) {
          this.currentMenuItemSignal.set(menuItem);
          this.nextMenuItemSignal.set(null);
        }
      } else {
        this.currentMenuItemSignal.set(this.pillService.getCurrentMenuItem());
        this.nextMenuItemSignal.set(this.pillService.getNextAvailableMenuItem());
      }

      if (this.currentMenuItemSignal()?.type === SurveyType.Who5) {
        if (!this.isOnDemand && !this.currentMenuItemSignal()?.isAvailable) {
          this.videoSource = 'mountain';
          this.allVideoSources = ['mountain'];
          this.surveyStepSignal.set(SurveyStep.Error);

          return;
        }

        this.videoSource = 'mountain';
        this.allVideoSources = [...this.survey.questions.map(question => question.video), 'river', 'beach-sunset'];
        this.startWho5Survey();
      } else if (this.currentMenuItemSignal()?.type === SurveyType.Body) {
        this.videoSource = 'wawes_body';
        this.allVideoSources = ['wawes_body', 'beach-sunset'];
        this.loadBodySurvey();
      } else if (this.currentMenuItemSignal()?.type === SurveyType.Comment) {
        this.videoSource = 'river';
        this.allVideoSources = ['river', 'beach-sunset'];
        this.loadFeedbackSurvey(this.currentMenuItemSignal()?.id as number);
      } else {
        this.videoSource = 'mountain';
        this.allVideoSources = ['mountain', 'beach-sunset'];
        this.loadCustomSurvey();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      this.isOnDemand = params['onDemand'] === 'true';
      this.onDemandType = params['type'];
    });
    this.accountData = this.sharedService.getAccountData();
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  onVideoChange(video: string): void {
    this.videoSource = video;
  }

  goToMenu(): void {
    this.router.navigate(['/available-surveys']);
  }

  startWho5Survey(): void {
    this.surveyService.startAnswer(this.isOnDemand).then(answerId => {
      this.answerId = answerId;
    });
  }

  async onWho5SurveyFinished(): Promise<void> {
    this.disabled = true;
    this.surveyAnswer = {
      AnswerId: this.answerId,
      Q1: this.survey.questions[0].answer,
      Q2: this.survey.questions[1].answer,
      Q3: this.survey.questions[2].answer,
      Q4: this.survey.questions[3].answer,
      Q5: this.survey.questions[4].answer,
      Q1TT: this.survey.questions[0].timeTaken,
      Q2TT: this.survey.questions[1].timeTaken,
      Q3TT: this.survey.questions[2].timeTaken,
      Q4TT: this.survey.questions[3].timeTaken,
      Q5TT: this.survey.questions[4].timeTaken,
      IsOnDemand: this.isOnDemand,
    };

    if (this.isOnDemand) {
      await this.surveyService.saveAnswer(this.surveyAnswer);
      this.router.navigate(['/on-demand'], { queryParams: { type: 'mind' } });
    } else {
      const feedbackMenuItem = this.pillService.getMenuItems().find(item => item.type === SurveyType.Comment);

      if (feedbackMenuItem?.isAvailable) {
        this.onVideoChange('river');
        this.loadFeedbackSurvey(feedbackMenuItem.id);
      } else {
        this.surveyService.saveAnswer(this.surveyAnswer).then(intervention => {
          this.surveyIntervention = intervention;
          this.setDoneSurveyStep();

          setTimeout(() => {
            if (this.surveyIntervention) {
              this.goToIntervention(this.surveyIntervention);
            }
          }, 3000);
        }).catch(() => {
          this.disabled = false;
        });
      }
    }
  }

  async onBodySurveyFinished(bodyParts: Record<BodyPartLabel, BodyPart> | null): Promise<void> {
    if (!bodyParts) {
      return;
    }

    const answers = Object.entries(bodyParts).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: value.painLevel > 0 ? value.painLevel : null
    }), {} as Record<string, number | null>);

    const surveySessionAnswer: SurveySessionAnswer = {
      IsAnonymous: false,
      SingledOutHr: false,
      OnDemand: this.isOnDemand,
      Answers: answers
    };

    if (!this.isOnDemand) {
      this.setDoneSurveyStep();
    }

    if (this.surveySession?.surveySessionId) {
      this.surveyIntervention = await this.surveyService.saveSurveySessionAnswer(this.surveySession.surveySessionId, surveySessionAnswer);
    }

    if (this.isOnDemand) {
      this.router.navigate(['/on-demand'], { queryParams: { type: 'body' } });
    } else {
      this.surveyStepSignal.set(SurveyStep.Intervention);
      this.interventionService.setIntervention(this.surveyIntervention, 'body');
      this.router.navigate(['/survey-intervention', 'body']);
      this.onVideoChange('river');
    }
  }

  async startSurveySession(surveyId: number, isOnDemand = false): Promise<void> {
    try {
      this.surveySession = await this.surveyService.startSurveySession(surveyId, isOnDemand);
    } catch (error) {
      this.surveyStepSignal.set(SurveyStep.Error);
    }
  }

  async loadBodySurvey(): Promise<void> {
    await this.startSurveySession(this.currentMenuItemSignal()?.id as number, this.isOnDemand);
    this.surveyService.getBodyPrefillFromPreviousAnswer().then(prefill => {
      if (prefill) {
        const hasPain = Object.values(prefill).some(bodyPartPain => bodyPartPain > 0);

        if (hasPain) {
          this.bodyPrefill = prefill;
        }
      }
    });
  }

  async loadFeedbackSurvey(surveyId: number): Promise<void> {
    this.disabled = false;
    this.surveyStepSignal.set(SurveyStep.Feedback);
    await this.startSurveySession(surveyId, false);

    this.surveyService.getRecipientList().then(recipients => {
      this.recipients = recipients;
    });
  }

  async onFeedbackSubmit(feedbackFormValue: FeedbackFormValue): Promise<void> {
    const surveySessionAnswer: SurveySessionAnswer = {
      IsAnonymous: feedbackFormValue.isAnonymous,
      ManagerRecipients: feedbackFormValue.managerRecipients.map(r => Number(r?.userId)),
      HrRecipients: feedbackFormValue.hrRecipients.map(r => Number(r?.userId)),
      SingledOutHr: (feedbackFormValue.hrRecipients?.length || 0) > 0,
      OnDemand: false,
      Answers: {
        [this.surveySession.steps[0].key as string]: feedbackFormValue.message as string
      }
    };

    this.setDoneSurveyStep();
    setTimeout(async () => {
      if (this.surveyAnswer) {
        const surveyIntervention = await this.surveyService.saveAnswer(this.surveyAnswer);
        this.goToIntervention(surveyIntervention);
      } else {
        this.router.navigate(['/available-surveys']);
      }
    }, 3000);

    if (this.surveySession.surveySessionId) {
      await this.surveyService.saveSurveySessionAnswer(this.surveySession.surveySessionId, surveySessionAnswer);
    }
  }

  async onEmptyFeedbackSubmit(): Promise<void> {
    if (this.disabled) {
      return;
    }

    this.disabled = true;
    const currentMenuItemId = this.pillService.getCurrentMenuItem()?.id;
    await this.surveyService.saveEmptyFeedback(this.surveySession.surveySessionId);

    const nextAvailable = this.pillService.getNextAvailableMenuItem();

    if (nextAvailable && nextAvailable?.isAvailable) {
      this.surveyStepSignal.set(SurveyStep.Survey);
      this.pillService.setSelectedMenuItemById(nextAvailable.id);

      this.currentMenuItemSignal.set(this.pillService.getCurrentMenuItem());
      this.nextMenuItemSignal.set(this.pillService.getNextAvailableMenuItem());

      this.pillService.finishMenuItem(currentMenuItemId);
      await this.pillService.loadMenuItems(true);
    } else {
      this.pillService.finishMenuItem(currentMenuItemId);
      await this.pillService.loadMenuItems(true);
      this.router.navigate(['/available-surveys']);
    }
  }

  async loadCustomSurvey(): Promise<void> {
    const surveyId = this.currentMenuItemSignal()?.id as number;

    if (!surveyId) {
      return;
    }

    await this.startSurveySession(surveyId, false);
  }

  async onCustomSurveyFinished(surveyAnswers: SurveySessionAnswer['Answers']): Promise<void> {
    const surveySessionAnswer: SurveySessionAnswer = {
      IsAnonymous: false,
      SingledOutHr: false,
      OnDemand: false,
      Answers: surveyAnswers
    };

    this.setDoneSurveyStep();
    setTimeout(() => {
      this.router.navigate(['/available-surveys']);
    }, 3000);

    if (this.surveySession.surveySessionId) {
      await this.surveyService.saveSurveySessionAnswer(this.surveySession.surveySessionId, surveySessionAnswer);
      await this.pillService.loadMenuItems(true);
    }
  }

  setDoneSurveyStep(): void {
    this.videoSource = 'beach-sunset';
    this.surveyStepSignal.set(SurveyStep.Done);
    this.pillService.finishMenuItem();
  }

  goBack(): void {
    const isFeedbackStep = this.surveyStepSignal() === SurveyStep.Feedback;
    const isWho5Survey = this.currentMenuItemSignal()?.type === SurveyType.Who5;

    if (isFeedbackStep && isWho5Survey && !this.isOnDemand) {
      this.surveyStepSignal.set(SurveyStep.Survey);
    } else {
      this.router.navigate(['/available-surveys']);
    }
  }

  async onSkipToIntervention(): Promise<void> {
    if (this.disabled) {
      return;
    }

    this.disabled = true;
    const currentMenuItemId = this.pillService.getCurrentMenuItem()?.id;
    if (this.surveySession?.surveySessionId) {
      await this.surveyService.saveEmptyFeedback(this.surveySession.surveySessionId);
    }

    this.pillService.finishMenuItem(currentMenuItemId);
    this.setDoneSurveyStep();

    if (this.surveyAnswer) {
      this.surveyIntervention = await this.surveyService.saveAnswer(this.surveyAnswer);
    }

    await this.pillService.loadMenuItems(true);

    setTimeout(async () => {
      if (this.surveyIntervention) {
        this.goToIntervention(this.surveyIntervention);
      }
    }, 3000);
  }

  goToIntervention(surveyIntervention: SurveyIntervention): void {
    this.surveyStepSignal.set(SurveyStep.Intervention);
    this.interventionService.setIntervention(surveyIntervention, 'mind');
    this.router.navigate(['/survey-intervention', 'mind']);
    this.onVideoChange('river');
  }
}
